import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useWithdrawCertification } from '@/api/hooks/usePortal';
import type { PortalCertification } from '@/api/types/assessment.types';

const CONSEQUENCES = [
  'Make it no longer usable as evidence for vendor assessments',
  "Be reflected in your tenants' vendor portfolios as a Withdrawn certification",
  'Make it no longer available for bypass attestation',
  'Disable access to related evidence files per our current retention model (the file is not deleted; it is no longer accessible through the application)',
  'Be audit-logged',
  'Require re-upload and re-submission if you want to reinstate this certification later',
];

interface Props {
  cert: PortalCertification | null;
  onClose: () => void;
}

export function WithdrawCertificationModal({ cert, onClose }: Props) {
  const withdrawMutation = useWithdrawCertification();
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('');

  function handleClose() {
    if (withdrawMutation.isPending) return;
    setConfirmed(false);
    setReason('');
    onClose();
  }

  function handleWithdraw() {
    if (!cert || !confirmed) return;
    withdrawMutation.mutate(
      { certId: cert.id, withdrawal_reason: reason.trim() || null },
      {
        onSuccess: () => {
          toast.success('Certification withdrawn');
          handleClose();
        },
        onError: () => {
          toast.error('Failed to withdraw certification');
        },
      },
    );
  }

  return (
    <Dialog open={!!cert} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-accent-amber shrink-0" />
            <DialogTitle>Withdraw certification?</DialogTitle>
          </div>
          {cert && (
            <DialogDescription className="text-sm text-text-secondary mt-1">
              {cert.cert_type}{cert.issuing_body ? ` — ${cert.issuing_body}` : ''}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-1">
          <p className="text-sm text-text-secondary">
            Withdrawing this certification will:
          </p>
          <ul className="space-y-1.5 text-sm text-text-secondary">
            {CONSEQUENCES.map((c) => (
              <li key={c} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-amber" />
                {c}
              </li>
            ))}
          </ul>

          <div className="space-y-1.5">
            <Label htmlFor="wr-reason" className="text-xs text-text-secondary">
              Reason <span className="text-text-secondary/60">(optional)</span>
            </Label>
            <textarea
              id="wr-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Briefly describe why you are withdrawing this certification…"
              className="w-full resize-none rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-cyan"
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-accent-amber"
            />
            <span className="text-sm text-text-primary">
              I understand that withdrawing this certification will make it no longer usable as evidence, will be reflected as Withdrawn in affected tenants' portfolios, will disable access to related evidence in the application, and will be audit-logged.
            </span>
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={withdrawMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleWithdraw}
            disabled={!confirmed || withdrawMutation.isPending}
          >
            {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw certification'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
